import "dayjs/locale/en"
import "dayjs/locale/fr"

import dayjs from "dayjs"
import { useField, useFormikContext } from "formik"
import React, { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native"

import useTheme from "../../features/Theme/ThemeProvider"
import { Caption, Subtitle1 } from "../Texts"
import { Touchable } from "../Touchable"
import { VectorIcon, VectorIconProps } from "../VectorIcon"
import { InputContainerStyle } from "./InputContainer"

interface IProps {
  name: string
  label?: string
  format?: string
  showModal?: boolean
  mode: "time" | "date"
  onPress?: () => void
  optionalValue?: string
  underlineColor?: string
  styles?: InputContainerStyle
  touchableStyle?: StyleProp<ViewStyle>
  icons?: { left?: VectorIconProps; right?: VectorIconProps }
  initialValue?: Date
  isChecked?: boolean
  dateType?: "end-date" | "start-date"
  noAdditionalValue?: boolean
}

export const DateTimePicker: FC<IProps> = ({
  label,
  name,
  styles,
  mode,
  icons,
  onPress,
  optionalValue,
  underlineColor,
  touchableStyle,
  initialValue,
  isChecked,
  dateType,
  noAdditionalValue,
}) => {
  const {
    colors: { surface, primary, error },
    dimensions: { spacing },
  } = useTheme()
  const { i18n } = useTranslation()

  const isFrench = i18n.language.startsWith("fr")

  // Formik fields and states
  const [field, meta] = useField<Date>(name)
  const { setFieldValue, setTouched } = useFormikContext()
  // Local fields and states
  const isDate = mode === "date"
  const isError = meta.touched && meta.error

  const formatValue = useMemo(() => {
    if (optionalValue) {
      return optionalValue
    }

    const date = dayjs(field.value)
    if (isDate) {
      return date.utc().format("L")
    }

    const timeFormat = isFrench ? "HH:mm" : "h:mm A"
    return date.locale(i18n.language).format(timeFormat)
  }, [optionalValue, field.value, isDate, i18n.language, isFrench])

  const s = useMemo(
    () => ({
      input: [
        defaultStyles.input,
        {
          paddingHorizontal: spacing,
          backgroundColor: surface.input,
        },
        touchableStyle,
      ],
      inputContent: [
        defaultStyles.inputContent,
        {
          paddingHorizontal: spacing / 2,
        },
      ],
      caption: isError
        ? styles?.error ?? {
            color: error,
          }
        : undefined,
      underlineColor: { height: 1, backgroundColor: isError ? error : underlineColor ?? primary },
      hiddenInput: defaultStyles.hiddenInput,
    }),
    [
      spacing,
      surface.input,
      touchableStyle,
      isError,
      styles?.error,
      error,
      underlineColor,
      primary,
    ],
  )

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      if (!value) {
        return
      }

      let selectedDate: Date
      if (isDate) {
        // Pour les dates, on crée une date UTC
        selectedDate = dayjs(value).utc(true).toDate()
      } else {
        // Pour les heures, on utilise la date actuelle avec l'heure sélectionnée
        const [hours, minutes] = value.split(":")
        selectedDate = dayjs(field.value).hour(parseInt(hours)).minute(parseInt(minutes)).toDate()
      }

      setTouched({ [name]: true })
      setFieldValue(name, selectedDate)
    },
    [field.value, name, setFieldValue, setTouched, isDate],
  )

  const onInputClick = useCallback(() => {
    if (initialValue) {
      if (!noAdditionalValue) {
        setFieldValue(`${name}_unknown`, false)
      }
      if (!field.value) {
        setFieldValue(name, initialValue)
      }
    }
  }, [setFieldValue, initialValue, name, noAdditionalValue, field.value])

  const inputValue = useMemo(() => {
    if (!field.value) {
      return ""
    }
    const date = dayjs(field.value)
    if (isDate) {
      return date.format("YYYY-MM-DD")
    }
    return date.format("HH:mm")
  }, [field.value, isDate])

  const minValue = useMemo(() => {
    if (!initialValue) {
      return undefined
    }
    const date = dayjs(initialValue)
    if (dateType === "start-date") {
      return isDate ? date.format("YYYY-MM-DD") : date.format("HH:mm")
    }
    return undefined
  }, [initialValue, dateType, isDate])

  const maxValue = useMemo(() => {
    if (!initialValue) {
      return undefined
    }
    const date = dayjs(initialValue)
    if (dateType === "end-date") {
      return isDate ? date.format("YYYY-MM-DD") : date.format("HH:mm")
    }
    return undefined
  }, [initialValue, dateType, isDate])

  return (
    <View style={styles?.child}>
      <Touchable
        rippleColor={primary}
        style={s.input}
        onPress={
          onPress ??
          (() => {
            onInputClick()
            // Focus sur l'input caché
            const input = document.getElementById(`date-input-${name}`) as HTMLInputElement
            input?.showPicker?.()
          })
        }
      >
        <>
          {icons?.left ? <VectorIcon {...icons?.left} /> : null}
          <View style={s.inputContent}>
            {label ? (
              <Caption emphasis="medium" style={s.caption}>
                {label}
              </Caption>
            ) : null}
            {!isChecked && field.value ? (
              <Subtitle1 emphasis="high">{formatValue}</Subtitle1>
            ) : null}
          </View>
          {icons?.right ? <VectorIcon {...icons?.right} /> : null}
          {/* Input HTML natif caché */}
          <input
            id={`date-input-${name}`}
            type={isDate ? "date" : "time"}
            value={inputValue}
            onChange={onChange}
            onClick={onInputClick}
            min={minValue}
            max={maxValue}
            style={hiddenInputWebStyle}
          />
        </>
      </Touchable>
      <View style={s.underlineColor} />
      {isError ? <Caption style={s.caption}>{meta.error}</Caption> : null}
    </View>
  )
}

const defaultStyles = StyleSheet.create({
  input: {
    height: 56,
    alignItems: "center",
    flexDirection: "row",
    position: "relative",
  },
  inputContent: {
    flex: 1,
    flexDirection: "column",
  },
  hiddenInput: {
    position: "absolute",
    opacity: 0,
    width: "100%",
    height: "100%",
    left: 0,
    top: 0,
  },
})
const hiddenInputWebStyle: React.CSSProperties = {
  ...(StyleSheet.flatten(defaultStyles.hiddenInput) as React.CSSProperties),
  cursor: "pointer",
}
